import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Message, MessageProps } from "primereact/message";
import { useState } from "react";
import { CatRunChangeResult } from "../../../client/types.gen";
import { catRunStatusBanner, reasonDetailLabel } from "./staleResults";
import styles from "./CatRunStatusIndicator.module.css";

export type StatusRow = { summary: string; details: string[] };

const StatusRowView: React.FC<StatusRow> = ({ summary, details }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className={styles.row}>
      <div className={styles.header}>
        <span>{summary}</span>
        {details.length > 0 && (
          <Button
            text
            size="small"
            label={showDetails ? "Hide details" : "Show details"}
            icon={showDetails ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            onClick={() => setShowDetails((shown) => !shown)}
          />
        )}
      </div>
      {showDetails && (
        <div className={styles.reasonChips}>
          {details.map((detail) => (
            <Chip key={detail} label={detail} />
          ))}
        </div>
      )}
    </div>
  );
};

// One-line summaries, each with its details as chips in a collapsible section.
export const CatStatusBanner: React.FC<{
  severity: MessageProps["severity"];
  title?: string;
  rows: StatusRow[];
}> = ({ severity, title, rows }) => (
  <Message
    severity={severity}
    className={styles.banner}
    content={
      <div className={styles.content}>
        {title && <span className={styles.title}>{title}</span>}
        {rows.map((row) => (
          <StatusRowView key={row.summary} {...row} />
        ))}
      </div>
    }
  />
);

// Detailed status banner for the selected vendor — the summary plus its
// specific triggers in a collapsible section. The at-a-glance per-vendor
// version lives on the model tabs (CatModelSwitcher); this explains the "why".
export const CatRunStatusIndicator: React.FC<{
  result: CatRunChangeResult;
}> = ({ result }) => {
  // Non-null for exactly the states worth surfacing, so this also gates the
  // banner.
  const banner = catRunStatusBanner(result);
  if (!banner) return null;

  return (
    <CatStatusBanner
      severity={banner.severity}
      rows={[
        {
          summary: banner.summary,
          details: result.reasons.map(reasonDetailLabel),
        },
      ]}
    />
  );
};
